/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: HttpServerPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the httpServer module.
----------------------------------------------------------------*/


import React from 'react';
import { Globe, Lock, FileText } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useHttpServer } from './hooks/useHttpServer';
import { FormBox } from '../../components/ui/FormBox';
import Toggle from '../../components/ui/Toggle';
import { Input } from '../../components/ui/input';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { ModuleFormHeader } from '../../components/layouts';
import { HttpServerField } from './components/HttpServerField';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { DEFAULT_HTTP_SERVER_CONFIG } from './types';
import type { TlsVersion, CipherProfile, ErrorLogLevel } from './types';

/** Shared CSS classes for native <select> controls */
const SELECT_CLS =
  'w-full bg-slate-900/50 border border-slate-700/60 rounded-md px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary/50 cursor-pointer font-sans transition-all duration-150';

export default function HttpServerPage() {
  const { t } = useI18n();
  const { formData, certificates, loading, saving, hasChanges, updateField, saveConfig, cancelChanges } =
    useHttpServer();



  const reset = t('httpServer.tooltips.resetField', 'Reset to default');

  /** Wraps saveConfig() to match FixedActionBar's void-returning onSave signature */
  const handleSave = async () => { await saveConfig(); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const certOptions = certificates.map((cert) => ({
    value: String(cert.id),
    label: cert.name,
    subLabel: cert.commonName || undefined
  }));

  return (
    <div className="p-6 pb-20">
      <ModuleFormHeader
        icon={<Globe className="h-5 w-5 text-primary" />}
        title={t('httpServer.title', 'HTTP/HTTPS Server')}
        backButtonText=""
        onBack={() => {}}
        hideBackButton={true}
      />

      <div className="mt-10 space-y-4">
        {/* -- Listeners ---------------------------------------------------- */}
        <FormBox
          title={t('httpServer.sections.listeners', 'Listeners')}
          description={t('httpServer.sections.listenersDesc', 'Configure server ports and binding')}
          icon={<Globe className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <HttpServerField id="httpPort" label={t('httpServer.fields.httpPort', 'HTTP Port')} tooltip={t('httpServer.tooltips.httpPort', 'Port for unencrypted HTTP traffic (default: 80)')} currentValue={formData.httpPort} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.httpPort} onReset={() => updateField('httpPort', DEFAULT_HTTP_SERVER_CONFIG.httpPort)} resetTooltip={reset}>
              <Input id="httpPort" type="number" value={formData.httpPort} onChange={(e) => updateField('httpPort', parseInt(e.target.value) || 80)} min={1} max={65535} />
            </HttpServerField>

            <HttpServerField id="httpsPort" label={t('httpServer.fields.httpsPort', 'HTTPS Port')} tooltip={t('httpServer.tooltips.httpsPort', 'Port for encrypted HTTPS traffic (default: 443)')} currentValue={formData.httpsPort} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.httpsPort} onReset={() => updateField('httpsPort', DEFAULT_HTTP_SERVER_CONFIG.httpsPort)} resetTooltip={reset}>
              <Input id="httpsPort" type="number" value={formData.httpsPort} onChange={(e) => updateField('httpsPort', parseInt(e.target.value) || 443)} min={1} max={65535} />
            </HttpServerField>

            <HttpServerField id="bindAddress" label={t('httpServer.fields.bindAddress', 'Bind Address')} tooltip={t('httpServer.tooltips.bindAddress', 'IP address to listen on (0.0.0.0 for all interfaces)')} currentValue={formData.bindAddress} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.bindAddress} onReset={() => updateField('bindAddress', DEFAULT_HTTP_SERVER_CONFIG.bindAddress)} resetTooltip={reset}>
              <Input id="bindAddress" type="text" value={formData.bindAddress} onChange={(e) => updateField('bindAddress', e.target.value)} />
            </HttpServerField>

            <HttpServerField id="enableHttp" label={t('httpServer.fields.enableHttp', 'Enable HTTP')} tooltip={t('httpServer.tooltips.enableHttp', 'Enable unencrypted HTTP listener')} currentValue={formData.enableHttp} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.enableHttp} onReset={() => updateField('enableHttp', DEFAULT_HTTP_SERVER_CONFIG.enableHttp)} resetTooltip={reset}>
              <Toggle value={formData.enableHttp} onChange={(v) => updateField('enableHttp', v)} />
            </HttpServerField>

            <HttpServerField id="enableHttps" label={t('httpServer.fields.enableHttps', 'Enable HTTPS')} tooltip={t('httpServer.tooltips.enableHttps', 'Enable encrypted HTTPS listener')} currentValue={formData.enableHttps} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.enableHttps} onReset={() => updateField('enableHttps', DEFAULT_HTTP_SERVER_CONFIG.enableHttps)} resetTooltip={reset}>
              <Toggle value={formData.enableHttps} onChange={(v) => updateField('enableHttps', v)} />
            </HttpServerField>

            <HttpServerField id="forceHttps" label={t('httpServer.fields.forceHttps', 'Force HTTPS')} tooltip={t('httpServer.tooltips.forceHttps', 'Automatically redirect HTTP traffic to HTTPS')} currentValue={formData.forceHttps} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.forceHttps} onReset={() => updateField('forceHttps', DEFAULT_HTTP_SERVER_CONFIG.forceHttps)} resetTooltip={reset}>
              <Toggle value={formData.forceHttps} onChange={(v) => updateField('forceHttps', v)} />
            </HttpServerField>
          </div>
        </FormBox>

        {/* -- HTTPS Policy ------------------------------------------------- */}
        <FormBox
          title={t('httpServer.sections.httpsPolicy', 'HTTPS Policy')}
          description={t('httpServer.sections.httpsPolicyDesc', 'TLS settings and security policies')}
          icon={<Lock className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <HttpServerField id="defaultCertificateId" label={t('httpServer.fields.defaultCertificate', 'Default SSL Certificate')} tooltip={t('httpServer.tooltips.defaultCertificate', 'Select active SSL certificate for HTTPS traffic and Nginx configuration')} currentValue={formData.defaultCertificateId ?? null} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.defaultCertificateId}>
              <SearchableSelect
                id="defaultCertificateId"
                value={formData.defaultCertificateId ? String(formData.defaultCertificateId) : null}
                onChange={(val) => updateField('defaultCertificateId', val ? parseInt(val) : null)}
                options={certOptions}
                placeholder={t('httpServer.placeholders.selectCert', 'Select Certificate…')}
                searchPlaceholder={t('common.search', 'Search…')}
                clearable={false}
              />
            </HttpServerField>

            <HttpServerField id="tlsMinVersion" label={t('httpServer.fields.tlsMinVersion', 'TLS Min Version')} tooltip={t('httpServer.tooltips.tlsMinVersion', 'Minimum TLS version to accept (1.2 recommended)')} currentValue={formData.tlsMinVersion} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.tlsMinVersion} onReset={() => updateField('tlsMinVersion', DEFAULT_HTTP_SERVER_CONFIG.tlsMinVersion)} resetTooltip={reset}>
              <select id="tlsMinVersion" value={formData.tlsMinVersion} onChange={(e) => updateField('tlsMinVersion', e.target.value as TlsVersion)} className={SELECT_CLS}>
                <option value="1.0">TLS 1.0</option>
                <option value="1.1">TLS 1.1</option>
                <option value="1.2">TLS 1.2</option>
                <option value="1.3">TLS 1.3</option>
              </select>
            </HttpServerField>

            <HttpServerField id="tlsMaxVersion" label={t('httpServer.fields.tlsMaxVersion', 'TLS Max Version')} tooltip={t('httpServer.tooltips.tlsMaxVersion', 'Maximum TLS version to accept')} currentValue={formData.tlsMaxVersion} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.tlsMaxVersion} onReset={() => updateField('tlsMaxVersion', DEFAULT_HTTP_SERVER_CONFIG.tlsMaxVersion)} resetTooltip={reset}>
              <select id="tlsMaxVersion" value={formData.tlsMaxVersion} onChange={(e) => updateField('tlsMaxVersion', e.target.value as TlsVersion)} className={SELECT_CLS}>
                <option value="1.0">TLS 1.0</option>
                <option value="1.1">TLS 1.1</option>
                <option value="1.2">TLS 1.2</option>
                <option value="1.3">TLS 1.3</option>
              </select>
            </HttpServerField>

            <HttpServerField id="cipherProfile" label={t('httpServer.fields.cipherProfile', 'Cipher Profile')} tooltip={t('httpServer.tooltips.cipherProfile', 'TLS cipher suite profile - Modern is most secure')} currentValue={formData.cipherProfile} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.cipherProfile} onReset={() => updateField('cipherProfile', DEFAULT_HTTP_SERVER_CONFIG.cipherProfile)} resetTooltip={reset}>
              <select id="cipherProfile" value={formData.cipherProfile} onChange={(e) => updateField('cipherProfile', e.target.value as CipherProfile)} className={SELECT_CLS}>
                <option value="modern">{t('httpServer.cipherProfiles.modern', 'Modern (Most Secure)')}</option>
                <option value="intermediate">{t('httpServer.cipherProfiles.intermediate', 'Intermediate')}</option>
                <option value="legacy">{t('httpServer.cipherProfiles.legacy', 'Legacy (Compatibility)')}</option>
              </select>
            </HttpServerField>

            <HttpServerField id="hstsEnabled" label={t('httpServer.fields.hstsEnabled', 'HSTS Enabled')} tooltip={t('httpServer.tooltips.hstsEnabled', 'Enable HTTP Strict Transport Security')} currentValue={formData.hstsEnabled} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.hstsEnabled} onReset={() => updateField('hstsEnabled', DEFAULT_HTTP_SERVER_CONFIG.hstsEnabled)} resetTooltip={reset}>
              <Toggle value={formData.hstsEnabled} onChange={(v) => updateField('hstsEnabled', v)} />
            </HttpServerField>

            {/* Conditional HSTS sub-fields */}
            {formData.hstsEnabled && (
              <>
                <HttpServerField id="hstsMaxAge" label={t('httpServer.fields.hstsMaxAge', 'HSTS Max Age')} tooltip={t('httpServer.tooltips.hstsMaxAge', 'Duration in seconds browsers remember HSTS')} currentValue={formData.hstsMaxAge} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.hstsMaxAge} onReset={() => updateField('hstsMaxAge', DEFAULT_HTTP_SERVER_CONFIG.hstsMaxAge)} resetTooltip={reset}>
                  <Input id="hstsMaxAge" type="number" value={formData.hstsMaxAge} onChange={(e) => updateField('hstsMaxAge', parseInt(e.target.value) || 31536000)} min={0} />
                </HttpServerField>

                <HttpServerField id="hstsIncludeSubdomains" label={t('httpServer.fields.hstsIncludeSubdomains', 'Include Subdomains')} tooltip={t('httpServer.tooltips.hstsIncludeSubdomains', 'Apply HSTS policy to all subdomains')} currentValue={formData.hstsIncludeSubdomains} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.hstsIncludeSubdomains} onReset={() => updateField('hstsIncludeSubdomains', DEFAULT_HTTP_SERVER_CONFIG.hstsIncludeSubdomains)} resetTooltip={reset}>
                  <Toggle value={formData.hstsIncludeSubdomains} onChange={(v) => updateField('hstsIncludeSubdomains', v)} />
                </HttpServerField>

                <HttpServerField id="hstsPreload" label={t('httpServer.fields.hstsPreload', 'HSTS Preload')} tooltip={t('httpServer.tooltips.hstsPreload', 'Allow inclusion in browser HSTS preload lists')} currentValue={formData.hstsPreload} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.hstsPreload} onReset={() => updateField('hstsPreload', DEFAULT_HTTP_SERVER_CONFIG.hstsPreload)} resetTooltip={reset}>
                  <Toggle value={formData.hstsPreload} onChange={(v) => updateField('hstsPreload', v)} />
                </HttpServerField>

                <div className="col-span-2" />
              </>
            )}
          </div>
        </FormBox>

        {/* -- Logging & Security ------------------------------------------- */}
        <FormBox
          title={t('httpServer.sections.loggingSecurity', 'Logging & Security')}
          description={t('httpServer.sections.loggingSecurityDesc', 'Access logging and security settings')}
          icon={<FileText className="h-5 w-5" />}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            <HttpServerField id="accessLogEnabled" label={t('httpServer.fields.accessLogEnabled', 'Access Log')} tooltip={t('httpServer.tooltips.accessLogEnabled', 'Enable HTTP access logging for all requests')} currentValue={formData.accessLogEnabled} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.accessLogEnabled} onReset={() => updateField('accessLogEnabled', DEFAULT_HTTP_SERVER_CONFIG.accessLogEnabled)} resetTooltip={reset}>
              <Toggle value={formData.accessLogEnabled} onChange={(v) => updateField('accessLogEnabled', v)} />
            </HttpServerField>

            <HttpServerField id="errorLogLevel" label={t('httpServer.fields.errorLogLevel', 'Error Log Level')} tooltip={t('httpServer.tooltips.errorLogLevel', 'Minimum severity level for error logging')} currentValue={formData.errorLogLevel} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.errorLogLevel} onReset={() => updateField('errorLogLevel', DEFAULT_HTTP_SERVER_CONFIG.errorLogLevel)} resetTooltip={reset}>
              <select id="errorLogLevel" value={formData.errorLogLevel} onChange={(e) => updateField('errorLogLevel', e.target.value as ErrorLogLevel)} className={SELECT_CLS}>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
                <option value="crit">Critical</option>
              </select>
            </HttpServerField>

            <HttpServerField id="maxRequestSize" label={t('httpServer.fields.maxRequestSize', 'Max Request Size')} tooltip={t('httpServer.tooltips.maxRequestSize', 'Maximum request body size in bytes (default: 10MB)')} currentValue={formData.maxRequestSize} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.maxRequestSize} onReset={() => updateField('maxRequestSize', DEFAULT_HTTP_SERVER_CONFIG.maxRequestSize)} resetTooltip={reset}>
              <Input id="maxRequestSize" type="number" value={formData.maxRequestSize} onChange={(e) => updateField('maxRequestSize', parseInt(e.target.value) || 10485760)} min={0} />
            </HttpServerField>

            <HttpServerField id="disableTlsRenegotiation" label={t('httpServer.fields.disableTlsRenegotiation', 'Disable TLS Renegotiation')} tooltip={t('httpServer.tooltips.disableTlsRenegotiation', 'Prevent TLS renegotiation attacks')} currentValue={formData.disableTlsRenegotiation} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.disableTlsRenegotiation} onReset={() => updateField('disableTlsRenegotiation', DEFAULT_HTTP_SERVER_CONFIG.disableTlsRenegotiation)} resetTooltip={reset}>
              <Toggle value={formData.disableTlsRenegotiation} onChange={(v) => updateField('disableTlsRenegotiation', v)} />
            </HttpServerField>

            <HttpServerField id="rateLimitEnabled" label={t('httpServer.fields.rateLimitEnabled', 'Rate Limiting')} tooltip={t('httpServer.tooltips.rateLimitEnabled', 'Enable basic rate limiting to protect against abuse')} currentValue={formData.rateLimitEnabled} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.rateLimitEnabled} onReset={() => updateField('rateLimitEnabled', DEFAULT_HTTP_SERVER_CONFIG.rateLimitEnabled)} resetTooltip={reset}>
              <Toggle value={formData.rateLimitEnabled} onChange={(v) => updateField('rateLimitEnabled', v)} />
            </HttpServerField>

            {/* Conditional rate-limit sub-fields */}
            {formData.rateLimitEnabled ? (
              <>
                <HttpServerField id="rateLimitRequests" label={t('httpServer.fields.rateLimitRequests', 'Max Requests')} tooltip={t('httpServer.tooltips.rateLimitRequests', 'Maximum number of requests per time window')} currentValue={formData.rateLimitRequests} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.rateLimitRequests} onReset={() => updateField('rateLimitRequests', DEFAULT_HTTP_SERVER_CONFIG.rateLimitRequests)} resetTooltip={reset}>
                  <Input id="rateLimitRequests" type="number" value={formData.rateLimitRequests} onChange={(e) => updateField('rateLimitRequests', parseInt(e.target.value) || 100)} min={1} />
                </HttpServerField>

                <HttpServerField id="rateLimitWindow" label={t('httpServer.fields.rateLimitWindow', 'Time Window (sec)')} tooltip={t('httpServer.tooltips.rateLimitWindow', 'Time window for rate limiting in seconds')} currentValue={formData.rateLimitWindow} defaultValue={DEFAULT_HTTP_SERVER_CONFIG.rateLimitWindow} onReset={() => updateField('rateLimitWindow', DEFAULT_HTTP_SERVER_CONFIG.rateLimitWindow)} resetTooltip={reset}>
                  <Input id="rateLimitWindow" type="number" value={formData.rateLimitWindow} onChange={(e) => updateField('rateLimitWindow', parseInt(e.target.value) || 60)} min={1} />
                </HttpServerField>

                <div className="col-span-2" />
              </>
            ) : (
              <div className="col-span-2" />
            )}
          </div>
        </FormBox>



      </div>

      {/* Sticky action bar */}
      <FixedActionBar
        hasChanges={hasChanges}
        onSave={handleSave}
        onCancel={cancelChanges}
        isSaving={saving}
        saveText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
      />
    </div>
  );
}
